/**
 * Short success / failure tones for floor scan flows, matching the Manufacturing-Dashboard Pre-Pack
 * custom widget (`pages/Pre-Pack/widgets/prePackTabs/prePackScanWidget.json` embedded script:
 * `playSuccessSound` / `playFailureSound` via Web Audio API).
 *
 * Browsers may leave `AudioContext` suspended until after a user gesture; we call `resume()` before each tone.
 */

let sharedContext: AudioContext | null = null;

type AudioContextConstructor = typeof AudioContext;

function resolveAudioContextCtor(): AudioContextConstructor | undefined {
  if (typeof globalThis === "undefined") {
    return undefined;
  }
  const globalWithWebkit = globalThis as typeof globalThis & {
    AudioContext?: AudioContextConstructor;
    webkitAudioContext?: AudioContextConstructor;
  };
  return globalWithWebkit.AudioContext ?? globalWithWebkit.webkitAudioContext;
}

function getAudioContext(): AudioContext | null {
  if (sharedContext) {
    return sharedContext;
  }
  const ctor = resolveAudioContextCtor();
  if (ctor == null) {
    return null;
  }
  try {
    sharedContext = new ctor();
  } catch {
    return null;
  }
  return sharedContext;
}

async function ensureAudioContextRunning(context: AudioContext): Promise<void> {
  if (context.state === "suspended") {
    await context.resume();
  }
}

function resumeAudioIfNeeded(context: AudioContext): void {
  if (context.state === "suspended") {
    void context.resume().catch(() => {
      /* autoplay policy — ignore */
    });
  }
}

function playTone(context: AudioContext, frequency: number, durationMs: number, startDelaySec: number): void {
  resumeAudioIfNeeded(context);
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = "sine";
  oscillator.frequency.value = frequency;
  gain.gain.value = 0.3;
  gain.gain.setValueAtTime(0.3, context.currentTime + startDelaySec);
  gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + startDelaySec + durationMs / 1000);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(context.currentTime + startDelaySec);
  oscillator.stop(context.currentTime + startDelaySec + durationMs / 1000);
}

function playToneWave(
  context: AudioContext,
  frequency: number,
  durationMs: number,
  startDelaySec: number,
  wave: OscillatorType,
): void {
  resumeAudioIfNeeded(context);
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.type = wave;
  oscillator.frequency.value = frequency;
  gain.gain.value = 0.3;
  gain.gain.setValueAtTime(0.3, context.currentTime + startDelaySec);
  gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + startDelaySec + durationMs / 1000);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(context.currentTime + startDelaySec);
  oscillator.stop(context.currentTime + startDelaySec + durationMs / 1000);
}

/** Ascending sine chimes (587 → 784 → 1047 Hz). */
export function playPairSuccessSound(): void {
  try {
    const context = getAudioContext();
    if (context === null) {
      return;
    }
    void ensureAudioContextRunning(context)
      .then(() => {
        playTone(context, 587, 80, 0);
        playTone(context, 784, 80, 0.08);
        playTone(context, 1047, 180, 0.16);
      })
      .catch(() => {
        /* autoplay policy — ignore */
      });
  } catch {
    /* decoding or autoplay quirks — intentionally silent */
  }
}

/** Two mid-range triangle beeps — idle warning (distinct from success chimes and failure squares). */
export function playLaborIdleWarningSound(): void {
  try {
    const context = getAudioContext();
    if (context === null) {
      return;
    }
    void ensureAudioContextRunning(context)
      .then(() => {
        playToneWave(context, 440, 180, 0, "triangle");
        playToneWave(context, 554, 220, 0.28, "triangle");
      })
      .catch(() => {
        /* autoplay policy — ignore */
      });
  } catch {
    /* intentionally silent */
  }
}

/** Three low square beeps (~200 Hz). */
export function playPairFailureSound(): void {
  try {
    const context = getAudioContext();
    if (context === null) {
      return;
    }
    playToneWave(context, 200, 150, 0, "square");
    playToneWave(context, 200, 150, 0.2, "square");
    playToneWave(context, 200, 200, 0.4, "square");
  } catch {
    /* intentionally silent */
  }
}

function playQuestionSuccessTones(context: AudioContext): void {
  playToneWave(context, 523, 110, 0, "triangle");
  playToneWave(context, 784, 110, 0.14, "triangle");
  playToneWave(context, 523, 220, 0.28, "triangle");
}

/**
 * Question-mark success: scan OK but operator must not treat it as normal potting flow
 * (triangle 523 → 784 → 523 Hz — distinct from ascending sine carton success and square failure).
 */
export function playPairQuestionSuccessSound(): void {
  try {
    const context = getAudioContext();
    if (context === null) {
      return;
    }
    void ensureAudioContextRunning(context)
      .then(() => {
        playQuestionSuccessTones(context);
      })
      .catch(() => {
        /* autoplay policy — ignore */
      });
  } catch {
    /* intentionally silent */
  }
}
